import {env} from '@/config';
import {LogLevel} from '@/constants';

const {ENVIRONMENT} = env;

export interface Logger {
  log(level: LogLevel, message: string, ...args: unknown[]): void;
  debug(message: string, ...args: unknown[]): void;
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, ...args: unknown[]): void;
}

type ConsoleMethods = Readonly<{
  debug: typeof console.debug;
  info: typeof console.info;
  warn: typeof console.warn;
  error: typeof console.error;
}>;

export class ConsoleLogger implements Logger {
  private logLevel: LogLevel;
  private consoleMethods: ConsoleMethods;
  private isProduction: boolean;

  constructor(logLevel: LogLevel = LogLevel.INFO) {
    this.logLevel = logLevel;
    this.consoleMethods = {
      debug: console.debug,
      info: console.info,
      warn: console.warn,
      error: console.error
    };
    this.isProduction = ENVIRONMENT === 'production';
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isProduction && level !== LogLevel.ERROR) {
      return false;
    }
    return LogLevel[level] >= LogLevel[this.logLevel];
  }

  private levelToConsoleMethod(level: LogLevel): keyof ConsoleMethods {
    switch (level) {
      case LogLevel.DEBUG:
        return 'debug';
      case LogLevel.INFO:
        return 'info';
      case LogLevel.WARN:
        return 'warn';
      case LogLevel.ERROR:
        return 'error';
    }
  }

  log(level: LogLevel, message: string, ...args: unknown[]): void {
    if (this.shouldLog(level)) {
      const consoleMethod = this.levelToConsoleMethod(level);
      this.consoleMethods[consoleMethod](message, ...args);
    }
  }

  debug(message: string, ...args: unknown[]): void {
    this.log(LogLevel.DEBUG, message, ...args);
  }

  info(message: string, ...args: unknown[]): void {
    this.log(LogLevel.INFO, message, ...args);
  }

  warn(message: string, ...args: unknown[]): void {
    this.log(LogLevel.WARN, message, ...args);
  }

  error(message: string, ...args: unknown[]): void {
    this.log(LogLevel.ERROR, message, ...args);
  }
}
